import { Maximize2, Radio, VideoOff, X } from "lucide-react";
import { useEffect, useState } from "react";
import { closeVideoSession, startVideoSession } from "../api";
import type { Camera, Vehicle, VideoSession } from "../types";

export function CameraTile({
  camera,
  vehicle,
}: {
  camera?: Camera;
  vehicle?: Vehicle;
}) {
  const online = Boolean(camera?.online && vehicle?.status !== "offline");
  const [session, setSession] = useState<VideoSession | null>(null);
  const [opening, setOpening] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => () => {
    if (session) void closeVideoSession(session.id);
  }, [session]);

  async function openLive() {
    if (!camera || !online) return;
    setOpening(true);
    setError("");
    try {
      setSession(await startVideoSession(camera.id));
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : "Transmisión no disponible");
    } finally {
      setOpening(false);
    }
  }

  function closeLive() {
    if (session) void closeVideoSession(session.id);
    setSession(null);
  }

  return (
    <article className={`camera-tile ${online ? "" : "camera-tile--offline"}`}>
      <button
        className={`camera-feed camera-feed--${camera?.channel || "front"}`}
        onClick={openLive}
        disabled={!online || opening}
        aria-label={`Abrir transmisión ${camera?.name || ""}`}
      >
        <div className="camera-feed__road"><span /><span /><span /></div>
        {!online && (
          <div className="camera-feed__offline">
            <VideoOff size={22} />
            SIN TRANSMISIÓN
          </div>
        )}
        {online && <span className="camera-feed__open">{opening ? "AUTORIZANDO…" : "ABRIR LIVE"}</span>}
        <div className="camera-feed__scanline" />
      </button>
      <header>
        <div>
          <span className={online ? "status-dot is-online" : "status-dot"} />
          <strong>{vehicle?.unit_code || "SIN UNIDAD"}</strong>
          <small>{camera?.name || "Canal disponible"}</small>
        </div>
        <button aria-label="Ampliar cámara" onClick={openLive} disabled={!online}>
          <Maximize2 size={14} />
        </button>
      </header>
      <footer>
        <span><Radio size={11} /> {online ? "LIVE" : "OFFLINE"}</span>
        <time>{new Date().toLocaleTimeString("es-MX", { hour12: false })}</time>
        <b>{vehicle ? `${vehicle.speed_kph} km/h` : "—"}</b>
      </footer>
      {error && <span className="camera-tile__error">{error}</span>}
      {session && (
        <div className="video-modal" role="dialog" aria-modal="true" aria-label="Transmisión en vivo">
          <div className="video-modal__bar">
            <div>
              <span className="status-dot is-critical" />
              <strong>{vehicle?.unit_code} · {camera?.name}</strong>
              <small>SESIÓN SEGURA · 02:00</small>
            </div>
            <button onClick={closeLive} aria-label="Cerrar transmisión"><X size={18} /></button>
          </div>
          {session.demo_mode ? (
            <div className={`video-modal__demo camera-feed camera-feed--${camera?.channel || "front"}`}>
              <div className="camera-feed__road"><span /><span /><span /></div>
              <div className="camera-feed__scanline" />
              <span>SIMULACIÓN MDVR · {session.provider.toUpperCase()}</span>
            </div>
          ) : (
            <video src={session.playback_url || undefined} autoPlay muted controls playsInline />
          )}
          <footer>
            <span><Radio size={12} /> LIVE AUDITADO</span>
            <b>{vehicle?.speed_kph} km/h · {vehicle?.route_name}</b>
          </footer>
        </div>
      )}
    </article>
  );
}
